import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-6" data-testid="privacy-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-8">Last updated: February 2026</p>

        <div className="prose prose-sm max-w-none space-y-6 text-gray-700">
          <section>
            <h2 className="text-xl font-semibold text-gray-900">1. Information We Collect</h2>
            <p>When you create an account on Badminton Stats, we collect:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Account Information:</strong> Email address, username, date of birth, and profile details you choose to provide.</li>
              <li><strong>Match Data:</strong> Scores, opponents, dates, and notes you log through the app.</li>
              <li><strong>Media:</strong> Videos and images you upload for Shorts, AI Coach analysis, or match highlights.</li>
              <li><strong>Usage Data:</strong> How you interact with the app, including features used and session duration.</li>
              <li><strong>Device Information:</strong> Browser type, operating system, and push notification tokens.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900">2. How We Use Your Information</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>To provide and improve our services, including match tracking, statistics, and AI coaching.</li>
              <li>To send push notifications about friend requests, likes, comments, and tags.</li>
              <li>To analyze uploaded videos using AI for coaching feedback (processed via secure third-party AI services).</li>
              <li>To display your public profile and stats to other users (based on your privacy settings).</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900">3. AI Video Analysis</h2>
            <p>
              When you use our AI Coach feature, your uploaded videos are temporarily processed to extract frames for analysis. 
              These frames are sent to a third-party AI service (Claude by Anthropic) for technique evaluation. 
              Video files are deleted from our servers after processing is complete. AI analysis results are stored in your account.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900">4. Data Sharing</h2>
            <p>We do not sell your personal data. We share information only:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>With other users based on your privacy settings (public, friends-only, or private).</li>
              <li>With AI service providers for video analysis (frames only, no personal data).</li>
              <li>When required by law or to protect our rights.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900">5. Data Retention & Deletion</h2>
            <p>
              You can delete your account at any time from your Profile page. This permanently removes all your data including 
              matches, posts, messages, AI sessions, and friend connections. Uploaded media files are deleted within 30 days of account deletion.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900">6. Security</h2>
            <p>
              We use industry-standard security measures including encrypted passwords (bcrypt), secure HTTPS connections, 
              and JWT-based authentication. However, no method of transmission over the Internet is 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900">7. Your Rights</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Access, update, or delete your personal data through your Profile settings.</li>
              <li>Control your profile visibility (public, friends-only, or private).</li>
              <li>Opt out of push notifications at any time.</li>
              <li>Request a copy of your data by contacting us.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900">8. Contact</h2>
            <p>For privacy questions, contact us at <strong>privacy@badmintonstats.app</strong>.</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
